import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useApi } from "~hooks/apiHooks/useApi";
import type { PermissionScopeResponse } from "~types/permissionScope";
import { INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY } from "~constants/permissions";
import { useGetLocationParams } from "~hooks/useGetLocationParams.ts";

const useGetCurrentUserPermissionScope = () => {
  const request = useApi();
  const { customerId, organizationId, sport } = useGetLocationParams();
  const navigate = useNavigate();

  return useQuery({
    queryKey: ["permission-scope-current-user", sport, organizationId],
    queryFn: () => {
      const shouldInvalidateCache = localStorage.getItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY) === "true";
      const url = shouldInvalidateCache
        ? `${sport}/o/${organizationId}/user/scope?invalidateCache=true`
        : `${sport}/o/${organizationId}/user/scope`
      localStorage.removeItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY);

      return request<PermissionScopeResponse>(url, {
        method: "GET",
        onError: {
          403: () =>  navigate(`/${customerId}/admin/${sport}/${organizationId}/403`, {
            state: { back: `/${customerId}/select/organization` },
          }),
          default: (response: Response, data: unknown) => navigate(`/${customerId}/admin/${sport}/${organizationId}/scope-error`, {
            state: {
              statusCode: response.status,
              error: JSON.stringify(data),
            }
          }),
        }
      }).then((response: PermissionScopeResponse) => ({
        scope: response?.data?.[0] || {},
        resources: response?.includes?.resources || {},
      }))
    },
    staleTime: () => 5 * 60 * 1000,
    enabled: !!organizationId && !!sport,
  });
};

export default useGetCurrentUserPermissionScope;
